'use client';

import type { InfiniteData, UseInfiniteQueryResult } from '@tanstack/react-query';
import { useMemo } from 'react';

import type { FormOption } from '@/types/general';
import type {
  InfiniteListDataResult,
  NodeToOptionConfig,
  PageData,
} from '@/types/infinite-query';

import useCombineInfiniteQueryData from './useCombineInfiniteQueryData';
import useInfiniteScroll from './useInfiniteScroll';

// Re-export types for convenience
export type { InfiniteListDataResult, NodeToOptionConfig } from '@/types/infinite-query';

/**
 * Deduplicates an array of nodes by a key extraction function
 * Preserves the first occurrence of each key
 */
function deduplicateByKey<TNode>(
  nodes: TNode[],
  getKey: (node: TNode) => string,
): TNode[] {
  const seenKeys = new Set<string>();
  return nodes.filter((node) => {
    const key = getKey(node);
    if (seenKeys.has(key) === true) {
      return false;
    }
    seenKeys.add(key);
    return true;
  });
}

/**
 * Creates a combinePages function that uses the provided key extractor
 * Combines pages from an infinite query into a single result with deduplication
 */
function createCombinePages<TNode>(
  getKey: (node: TNode) => string,
) {
  return function combinePages(
    pages: Array<PageData<TNode>>,
  ): PageData<TNode> | undefined {
    if (pages.length === 0) {
      return undefined;
    }

    const lastPage = pages[pages.length - 1];
    if (lastPage === undefined) {
      return undefined;
    }

    const allNodes = pages.flatMap(page => page.nodes);
    const uniqueNodes = deduplicateByKey(allNodes, getKey);

    return {
      nodes: uniqueNodes,
      pageInfo: lastPage.pageInfo,
    };
  };
}

/**
 * Converts nodes to form options with deduplication
 */
function nodesToOptions<TNode>(
  nodes: TNode[],
  config: NodeToOptionConfig<TNode>,
): FormOption[] {
  const seenValues = new Set<string>();
  const options: FormOption[] = [];

  for (const node of nodes) {
    const value = config.getValue(node);
    if (seenValues.has(value) === false) {
      seenValues.add(value);
      options.push({
        value,
        label: config.getLabel(node),
      });
    }
  }

  return options;
}

/**
 * Hook that combines infinite query result with scroll handling and deduplication
 * Provides a unified interface for consuming infinite list data in components
 *
 * @template TNode - The type of nodes in the list
 * @template TPageData - The type of each page in the infinite query (extends PageData)
 * @param infiniteQuery - The result from useInfiniteQuery
 * @param nodeToOption - Configuration for converting nodes to form options (getValue is used for deduplication)
 * @returns Combined and deduplicated list data with scroll handling
 *
 * @example
 * ```tsx
 * const spacesQuery = useInfiniteSpacesQuery(params);
 * const spacesData = useInfiniteListData(spacesQuery, {
 *   getValue: (space) => space.id,
 *   getLabel: (space) => space.id,
 * });
 *
 * return (
 *   <ComboBox
 *     options={spacesData.options}
 *     loading={spacesData.isLoading}
 *     loadMoreRef={spacesData.loadMoreRef}
 *     isFetchingNextPage={spacesData.isFetchingNextPage}
 *     hasNextPage={spacesData.hasNextPage}
 *   />
 * );
 * ```
 */
export function useInfiniteListData<
  TNode,
  TPageData extends PageData<TNode> = PageData<TNode>,
>(
  infiniteQuery: UseInfiniteQueryResult<InfiniteData<TPageData, unknown>, Error>,
  nodeToOption: NodeToOptionConfig<TNode>,
): InfiniteListDataResult<TNode> {
  const scrollResult = useInfiniteScroll(infiniteQuery);

  // Use getValue from nodeToOption for deduplication
  const combinePages = useMemo(
    () => createCombinePages<TNode>(nodeToOption.getValue),
    [nodeToOption.getValue],
  );

  const combinedData = useCombineInfiniteQueryData(
    infiniteQuery.data,
    combinePages as (pages: TPageData[]) => PageData<TNode> | undefined,
  );

  return useMemo(() => {
    const nodes = combinedData?.nodes ?? [];
    const options = nodesToOptions(nodes, nodeToOption);

    return {
      nodes,
      pageInfo: combinedData?.pageInfo,
      isLoading: infiniteQuery.isLoading,
      isFetchingNextPage: infiniteQuery.isFetchingNextPage,
      hasNextPage: scrollResult.hasNextPage ?? false,
      loadMoreRef: scrollResult.loadMoreRef,
      isError: infiniteQuery.isError,
      error: infiniteQuery.error,
      refetch: infiniteQuery.refetch,
      options,
    };
  }, [combinedData, infiniteQuery, scrollResult, nodeToOption]);
}

export default useInfiniteListData;
